import { useMantineColorScheme } from '@mantine/core';
import { 
  BRAND_COLORS, 
  SEMANTIC_COLORS, 
  CV_SECTION_PALETTE,
  COLOR_COMBINATIONS,
  DESIGN_PALETTES,
  GRADIENTS,
  BrandColor,
  UIColor,
  TechColor,
  StatusColor,
  ChatColor,
  ExportColor,
  Gradient,
  BrandColorKey,
  SemanticColorKey,
  CVSectionKey,
  ColorCombinationKey,
  DesignPaletteKey,
  GradientKey,
  getColorValue,
  getSemanticColor,
  getCVSectionColor,
  getDesignPalette,
  getGradient,
  getPaletteColor
} from '../constants/colorPalette';

export const useColors = () => {
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  // Direct access to named color constants
  const brand = BrandColor;
  const ui = UIColor;
  const tech = TechColor;
  const status = StatusColor;
  const chat = ChatColor;
  const exportColors = ExportColor;
  const gradients = Gradient;

  // Legacy methods for backward compatibility
  const getBrandColor = (key: BrandColorKey): string => {
    return getColorValue(key);
  };

  const getSemanticColorValue = (key: SemanticColorKey): string => {
    return getSemanticColor(key);
  };

  const getSectionColor = (key: CVSectionKey) => {
    return getCVSectionColor(key);
  };

  const getColorCombination = (key: ColorCombinationKey) => {
    return COLOR_COMBINATIONS[key];
  };

  const getDesignPaletteColors = (key: DesignPaletteKey) => {
    return getDesignPalette(key);
  };

  const getPaletteColorValue = (paletteKey: DesignPaletteKey, shade: keyof typeof DESIGN_PALETTES.gray): string => {
    return getPaletteColor(paletteKey, shade);
  };

  const getGradientValue = (key: GradientKey): string => {
    return getGradient(key);
  };

  const getAdaptiveColor = (lightColor: string, darkColor: string): string => {
    return isDark ? darkColor : lightColor;
  };

  const getContrastColor = (backgroundColor: string): string => {
    const darkColorValues = [
      BRAND_COLORS.red, BRAND_COLORS.darkBlue, BRAND_COLORS.purple, 
      BRAND_COLORS.darkPurple, BRAND_COLORS.darkGreen, BRAND_COLORS.black,
      BRAND_COLORS.darkGray
    ];
    
    return darkColorValues.includes(backgroundColor as any) ? BRAND_COLORS.white : BRAND_COLORS.black;
  };

  const getStatusColors = () => ({
    success: SEMANTIC_COLORS.success,
    warning: SEMANTIC_COLORS.warning,
    error: SEMANTIC_COLORS.error,
    info: SEMANTIC_COLORS.info,
  });

  const getUIColors = () => ({
    primary: SEMANTIC_COLORS.primary,
    secondary: SEMANTIC_COLORS.secondary,
    accent: SEMANTIC_COLORS.accent,
    background: getAdaptiveColor(
      SEMANTIC_COLORS.backgroundLight, 
      SEMANTIC_COLORS.backgroundDark
    ),
    surface: getAdaptiveColor(
      SEMANTIC_COLORS.backgroundLight,
      BRAND_COLORS.darkGray
    ),
    border: getAdaptiveColor(
      SEMANTIC_COLORS.borderLight,
      SEMANTIC_COLORS.borderDark
    ),
  });

  const getMaterialColor = (colorName: DesignPaletteKey, shade: keyof typeof DESIGN_PALETTES.gray = 500) => {
    return getPaletteColorValue(colorName, shade);
  };

  const getChatColors = () => ({
    background: getAdaptiveColor(
      SEMANTIC_COLORS.chatBackground,
      SEMANTIC_COLORS.chatBackgroundDark
    ),
    userMessage: getAdaptiveColor(
      getPaletteColorValue('blue', 50),
      getPaletteColorValue('blue', 900)
    ),
    botMessage: getAdaptiveColor(
      getPaletteColorValue('gray', 100),
      getPaletteColorValue('gray', 800)
    ),
  });

  const getActionColors = () => ({
    export: SEMANTIC_COLORS.exportError,
    team: BRAND_COLORS.teamBlue,
    favorite: BRAND_COLORS.complimentaryBlue,
  });

  return {
    // Named color constants (NEW - recommended approach)
    brand,
    ui,
    tech,
    status,
    chat,
    export: exportColors,
    gradients,
    
    // Direct access to color palettes
    brandColors: BRAND_COLORS,
    semanticColors: SEMANTIC_COLORS,
    cvSectionPalette: CV_SECTION_PALETTE,
    colorCombinations: COLOR_COMBINATIONS,
    designPalettes: DESIGN_PALETTES,
    
    // Legacy utility functions (for backward compatibility)
    getBrandColor,
    getSemanticColorValue,
    getSectionColor,
    getColorCombination,
    getDesignPaletteColors,
    getPaletteColorValue,
    getGradientValue,
    getAdaptiveColor,
    getContrastColor,
    getStatusColors,
    getUIColors,
    getMaterialColor,
    getChatColors,
    getActionColors,
    
    // Theme info
    isDark,
    colorScheme,
  };
}; 